import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, Star } from "lucide-react";

const empty = {
  client_id: "", name: "", slug: "", default_language: "en", supported_languages: ["en"],
  description: "", logo_url: "", cover_url: "", theme: "luxury_dark", accent_color: "#C4A47C",
  contact_config: { whatsapp: "", phone: "", email: "", lead_form_enabled: true, whatsapp_template: "Hello, I am interested in {unit_code} at {project_name}." },
};

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty);
  const [officialDemoId, setOfficialDemoId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [p, c, s] = await Promise.all([
        api.get("/admin/projects"),
        api.get("/admin/clients"),
        api.get("/admin/site-settings"),
      ]);
      setProjects(p.data); setClients(c.data);
      setOfficialDemoId(s.data?.official_demo_project_id || null);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const setOfficialDemo = async (id) => {
    const next = officialDemoId === id ? null : id;
    try {
      await api.put("/admin/site-settings", { official_demo_project_id: next });
      setOfficialDemoId(next);
      toast.success(next ? "Set as official demo — landing page updated" : "Cleared official demo");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const create = async () => {
    try {
      const body = { ...form, slug: form.slug || slugify(form.name) };
      const r = await api.post("/admin/projects", body);
      toast.success("Project created");
      setCreating(false);
      setForm(empty);
      navigate(`/studio/projects/${r.data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this project? All scenes, zones and units will be removed.")) return;
    await api.delete(`/admin/projects/${id}`);
    load();
  };

  return (
    <div className="px-8 py-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Studio</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Projects</h1>
        </div>
        <button onClick={() => { setCreating(true); setForm(empty); }} data-testid="add-project-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700">
          <Plus className="h-4 w-4" /> New project
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.length === 0 && (
          <div className="col-span-full text-sm text-zinc-500" data-testid="empty-projects">No projects yet.</div>
        )}
        {projects.map(p => {
          const c = clients.find(cc => cc.id === p.client_id);
          return (
            <div key={p.id} className="rounded-xl border border-zinc-200 bg-white overflow-hidden hover:shadow-md transition" data-testid={`project-card-${p.id}`}>
              <Link to={`/studio/projects/${p.id}`} className="block" data-testid={`open-project-card-${p.id}`}>
                <div className="aspect-[16/9] bg-zinc-100 relative">
                  {p.cover_url ? (
                    <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-400 text-xs">No cover</div>
                  )}
                  <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${p.is_published ? "bg-emerald-500 text-white" : "bg-zinc-900/80 text-white"}`}>
                      {p.is_published ? "Published" : "Draft"}
                    </span>
                    {officialDemoId === p.id && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-[#C4A47C] text-[#0B0E14] inline-flex items-center gap-1" data-testid={`official-demo-badge-${p.id}`}>
                        <Star className="h-3 w-3 fill-current" /> Official demo
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4 pb-2">
                  <div className="font-display text-lg">{p.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{c?.company_name || "—"} · /{p.slug}</div>
                </div>
              </Link>
              <div className="px-4 pb-4 flex items-center gap-2">
                <Link to={`/studio/projects/${p.id}`} className="px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs hover:bg-zinc-700" data-testid={`open-project-${p.id}`}>Open</Link>
                {p.is_published && (
                  <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-zinc-300 text-xs hover:bg-zinc-50" data-testid={`view-public-${p.id}`}>
                    <ExternalLink className="h-3 w-3" /> Public
                  </a>
                )}
                {p.is_published && (
                  <button
                    onClick={() => setOfficialDemo(p.id)}
                    data-testid={`set-official-demo-${p.id}`}
                    title="The landing page 'Explore Demo' button points to the official demo project"
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition ${officialDemoId === p.id ? "bg-[#C4A47C]/15 text-[#8a6d3b] border border-[#C4A47C]/40" : "border border-zinc-300 hover:bg-zinc-50"}`}>
                    <Star className={`h-3 w-3 ${officialDemoId === p.id ? "fill-current" : ""}`} />
                    {officialDemoId === p.id ? "Official demo" : "Set as demo"}
                  </button>
                )}
                <button onClick={() => del(p.id)} className="ml-auto p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`del-project-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setCreating(false)}>
          <div className="bg-white w-full max-w-lg rounded-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-xl mb-5">New project</div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-700 mb-1.5">Client</label>
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900"
                  data-testid="project-field-client">
                  <option value="">— Select —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-700 mb-1.5">Project name</label>
                <input data-testid="project-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugify(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900" />
              </div>
              <div>
                <label className="block text-xs text-zinc-700 mb-1.5">Slug</label>
                <input data-testid="project-field-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900 font-mono text-sm" />
              </div>
              <div>
                <label className="block text-xs text-zinc-700 mb-1.5">Description</label>
                <textarea data-testid="project-field-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900 h-20" />
              </div>
              <div>
                <label className="block text-xs text-zinc-700 mb-1.5">Cover image URL (optional)</label>
                <input data-testid="project-field-cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setCreating(false)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm">Cancel</button>
              <button onClick={create} className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid="project-create-submit">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
